/**
 * Design sketch renderer (Spec 007).
 *
 * Pure-functional SVG component — same input, same output. Renders a
 * Design payload as a labeled architectural elevation:
 * - Outer envelope as a stroked rectangle with overall dimensions
 *   labeled outside the top + right edges.
 * - Per-element rectangles with kind-specific styling (drawers
 *   horizontal handle, TV opening rounded inner stroke, shelves
 *   hairline horizontals, etc.).
 * - <title> on each element for screen readers; aria-label on the
 *   root SVG summarizing overall dimensions.
 *
 * Constitution III: every dimension came from the customer.
 */

import type {
  ChatMessageKind,
  Design,
  DesignElement,
  DesignKind,
} from "@starz/shared/api/types.gen";

interface Props {
  design: Design;
  // Phase 18.4 — when rendered from a `design_pending` chat message,
  // show approve / reject buttons under the sketch. When rendered from
  // a `design_approved` / `design_rejected` message, show the matching
  // chip and hide the buttons.
  kind?: ChatMessageKind;
  onApprove?: () => void;
  onReject?: () => void;
  disabled?: boolean;
}

// Visual constants (in SVG user units; we map 1 inch = 1 unit and
// use viewBox to scale to the actual rendered size). Margins around
// the envelope leave room for the dimension labels.
const MARGIN = 18;
const STROKE_W = 0.5;
const FONT_SIZE = 3.5;
const SMALL_FONT_SIZE = 2.5;

const KIND_STYLES: Record<DesignKind, { fill: string; stroke: string; strokeDash?: string }> = {
  rect: { fill: "#fafaf7", stroke: "#1E3D58" },
  drawer: { fill: "#f5efe6", stroke: "#1E3D58" },
  tv_opening: { fill: "#1f2937", stroke: "#1E3D58" },
  shelf: { fill: "transparent", stroke: "#8B6D2C" },
  door: { fill: "#fbf9f3", stroke: "#1E3D58" },
  cabinet: { fill: "#fbf9f3", stroke: "#1E3D58" },
};

function _kindLabel(kind: DesignKind): string {
  return {
    rect: "Section",
    drawer: "Drawer",
    tv_opening: "TV opening",
    shelf: "Shelf",
    door: "Door",
    cabinet: "Cabinet",
  }[kind];
}

/** Phase 18.6 — y in the schema is bottom-left origin (architectural
 * elevation): y=0 is the floor, y=outer_h is the ceiling. SVG's native
 * coord system is top-left, so we flip y for every element on render.
 * `flipY(logicalY, h)` returns the SVG-y of the TOP edge of an element
 * whose logical bottom is at `logicalY` and whose height is `h`. */
function flipY(logicalY: number, outerH: number, h: number = 0): number {
  return outerH - logicalY - h;
}

function ElementRect({ el, outerH }: { el: DesignElement; outerH: number }) {
  const { kind, x, y, w, h, label, material } = el;
  const style = KIND_STYLES[kind];
  const display = label ?? _kindLabel(kind);
  const dimsLabel = `${Math.round(w)} × ${Math.round(h)} in`;
  const labelInside = w >= 14 && h >= 7;
  // Screen-y of the rectangle's TOP edge (highest point on screen).
  const sy = flipY(y, outerH, h);
  // Screen-y of the rectangle's centre.
  const syCenter = flipY(y + h / 2, outerH);

  return (
    <g>
      <title>
        {display} — {dimsLabel}
        {material ? ` — ${material}` : ""}
      </title>
      <rect
        x={x}
        y={sy}
        width={w}
        height={h}
        fill={style.fill}
        stroke={style.stroke}
        strokeWidth={STROKE_W}
      />
      {kind === "drawer" && (
        // Subtle drawer-pull line near the architectural TOP of the
        // drawer (where you grab). In bottom-left coords that's 70% of
        // the height above the floor.
        <line
          x1={x + w * 0.3}
          y1={flipY(y + h * 0.7, outerH)}
          x2={x + w * 0.7}
          y2={flipY(y + h * 0.7, outerH)}
          stroke={style.stroke}
          strokeWidth={STROKE_W * 0.6}
        />
      )}
      {kind === "tv_opening" && (
        // Rounded inner stroke to suggest a screen.
        <rect
          x={x + 1}
          y={sy + 1}
          width={Math.max(0, w - 2)}
          height={Math.max(0, h - 2)}
          fill="none"
          stroke="#374151"
          strokeWidth={STROKE_W * 0.5}
          rx={1.5}
        />
      )}
      {kind === "shelf" && (
        // A shelf is essentially a hairline; emphasise that.
        <line
          x1={x}
          y1={syCenter}
          x2={x + w}
          y2={syCenter}
          stroke={style.stroke}
          strokeWidth={STROKE_W * 0.8}
        />
      )}
      {labelInside ? (
        <>
          <text
            x={x + w / 2}
            y={syCenter + (material ? -1 : 1)}
            fontSize={FONT_SIZE}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={kind === "tv_opening" ? "#f9fafb" : "#1E3D58"}
            fontFamily="Georgia, serif"
          >
            {display}
          </text>
          {material && (
            <text
              x={x + w / 2}
              y={syCenter + 3}
              fontSize={SMALL_FONT_SIZE}
              textAnchor="middle"
              dominantBaseline="middle"
              fontStyle="italic"
              fill={kind === "tv_opening" ? "#d1d5db" : "#7c7060"}
              fontFamily="Georgia, serif"
            >
              {material}
            </text>
          )}
        </>
      ) : (
        // Element too small to fit text inside — float caption below
        // the rectangle in screen-space (immediately under its
        // screen-y bottom edge).
        <text
          x={x + w / 2}
          y={sy + h + FONT_SIZE + 0.5}
          fontSize={SMALL_FONT_SIZE}
          textAnchor="middle"
          fill="#1E3D58"
          fontFamily="Georgia, serif"
        >
          {display}
        </text>
      )}
    </g>
  );
}

export function DesignSketch({
  design,
  kind,
  onApprove,
  onReject,
  disabled,
}: Props) {
  const { outer_w: outerW, outer_h: outerH, elements } = design;
  const viewBox = `${-MARGIN} ${-MARGIN} ${outerW + 2 * MARGIN} ${outerH + 2 * MARGIN}`;
  const dimsAria = `Design sketch — ${Math.round(outerW)} inches wide by ${Math.round(
    outerH,
  )} inches tall`;
  return (
    <section
      aria-label={dimsAria}
      className="rounded-xl border border-starz-gold/20 bg-starz-cream/30 p-3 shadow-sm"
    >
      <p className="mb-2 text-xs font-semibold uppercase tracking-widest text-starz-gold">
        Design sketch
      </p>
      <svg
        role="img"
        aria-label={dimsAria}
        viewBox={viewBox}
        xmlns="http://www.w3.org/2000/svg"
        className="block w-full max-w-2xl"
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Outer envelope */}
        <rect
          x={0}
          y={0}
          width={outerW}
          height={outerH}
          fill="#ffffff"
          stroke="#1E3D58"
          strokeWidth={STROKE_W * 1.4}
        />
        {/* Width label outside the top edge */}
        <text
          x={outerW / 2}
          y={-FONT_SIZE - 1}
          fontSize={FONT_SIZE}
          textAnchor="middle"
          fill="#1E3D58"
          fontFamily="Georgia, serif"
        >
          {Math.round(outerW)} in W
        </text>
        {/* Height label outside the right edge, vertical */}
        <text
          x={outerW + FONT_SIZE + 1}
          y={outerH / 2}
          fontSize={FONT_SIZE}
          textAnchor="middle"
          dominantBaseline="middle"
          transform={`rotate(90 ${outerW + FONT_SIZE + 1} ${outerH / 2})`}
          fill="#1E3D58"
          fontFamily="Georgia, serif"
        >
          {Math.round(outerH)} in H
        </text>
        {elements.map((el, i) => (
          <ElementRect key={i} el={el} outerH={outerH} />
        ))}
      </svg>
      <p className="mt-2 text-[10px] italic text-warm-gray">
        Sketch reflects what you've described. Final design subject to contractor confirmation.
      </p>
      {/* Phase 18.4 — approval row. Three terminal states:
       *   - design_pending: show Approve / Reject buttons
       *   - design_approved: show "✓ Approved" chip
       *   - design_rejected: show "Dismissed" chip
       * No-kind callers (legacy / streaming path) render nothing.
       * Falls back to the `design.approved_at` check so loaded
       * history shows the badge even if the kind wasn't pinned. */}
      {kind === "design_approved" || (kind === undefined && design.approved_at && !design.superseded_at) ? (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-800">
          ✓ Approved
        </p>
      ) : kind === "design_rejected" ? (
        <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-warm-gray/15 px-3 py-1 text-xs font-semibold text-warm-gray">
          Dismissed
        </p>
      ) : kind === "design_pending" && (onApprove || onReject) ? (
        <div className="mt-3 flex gap-2">
          {onReject ? (
            <button
              type="button"
              onClick={onReject}
              disabled={disabled}
              className="rounded-full border border-warm-gray/40 px-4 py-1.5 text-sm font-semibold text-starz-charcoal hover:bg-warm-gray/10 disabled:opacity-50"
            >
              Reject
            </button>
          ) : null}
          {onApprove ? (
            <button
              type="button"
              onClick={onApprove}
              disabled={disabled}
              className="rounded-full bg-starz-navy px-4 py-1.5 text-sm font-semibold text-starz-off-white hover:opacity-90 disabled:opacity-50"
            >
              Approve sketch
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
